import React, { useState, useEffect, useRef } from "react";

const Ads = ({ ads = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Duplicate ads if there are too few to create a proper 3D loop effect
  const displayAds = React.useMemo(() => {
    if (!ads || ads.length === 0) return [];
    if (ads.length === 1) return ads;
    if (ads.length === 2) return [...ads, ...ads, ...ads];
    if (ads.length === 3) return [...ads, ...ads];
    return ads;
  }, [ads]);

  const totalAds = displayAds.length;

  useEffect(() => {
    if (totalAds <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalAds);
    }, 3000);

    return () => clearInterval(interval);
  }, [totalAds]);

  if (totalAds === 0) return null;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalAds);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalAds) % totalAds);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      handleNext();
    }
    if (touchStartX.current - touchEndX.current < -50) {
      handlePrev();
    }
  };

  const getCardStyle = (index) => {
    let offset = (index - currentIndex) % totalAds;
    if (offset < 0) offset += totalAds;
    
    // Convert to relative positioning: e.g., -2, -1, 0, 1, 2
    if (offset > Math.floor(totalAds / 2)) {
      offset -= totalAds;
    }

    const isActive = offset === 0;
    const isVisible = Math.abs(offset) <= 2;

    let translateX = `${offset * 65}%`;
    let scale = 1 - Math.abs(offset) * 0.2; // 1, 0.8, 0.6
    let opacity = isActive ? 1 : Math.max(0, 1 - Math.abs(offset) * 0.4); // 1, 0.6, 0.2, 0
    let zIndex = 20 - Math.abs(offset);

    if (Math.abs(offset) > 2) {
      opacity = 0;
      scale = 0;
    }

    return {
      transform: `translateX(calc(-50% + ${translateX})) scale(${Math.max(0, scale)})`,
      opacity,
      zIndex,
      pointerEvents: isActive ? "auto" : "none",
      visibility: isVisible ? "visible" : "hidden",
    };
  };

  return (
    <div 
      className="premium-ads-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="carousel-track">
        {displayAds.map((ad, index) => (
          <a
            key={`${ad._id}-${index}`} 
            className={`carousel-card ad-card ${index === currentIndex ? 'active' : ''}`}
            style={getCardStyle(index)}
            href={ad.url || "#"}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="card-content">
              <span className="ad-badge">{ad.type || 'Announcement'}</span>
              
              <div className="ad-image-container">
                <img
                  src={ad.image || "https://via.placeholder.com/300x180"}
                  alt={ad.title || "Sponsored"}
                  className="ad-logo"
                />
              </div>

              {ad.title && (
                <h4 className="ad-title">{ad.title}</h4>
              )}
            </div>
          </a>
        ))}
      </div>
      
      {totalAds > 1 && (
        <>
          <button className="nav-btn prev-btn" onClick={(e) => { e.preventDefault(); handlePrev(); }} aria-label="Previous Ad">
            <i className="fa fa-chevron-left"></i>
          </button>
          <button className="nav-btn next-btn" onClick={(e) => { e.preventDefault(); handleNext(); }} aria-label="Next Ad">
            <i className="fa fa-chevron-right"></i>
          </button>
        </>
      )}
    </div>
  );
};

export default Ads;
